import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { listHubspotContacts } from '@/features/integrations/services/integration-service';
import type { HubspotContact } from '@/features/integrations/types/integration.types';
import type { UnifiedCustomer } from '../types/customer.types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: UnifiedCustomer | null;
}

export function HubspotCompanyDetailDrawer({ open, onOpenChange, company }: Props) {
  const [contacts, setContacts] = useState<HubspotContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !company || company.source !== 'HUBSPOT' || !company.integrationId) {
      setContacts([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    listHubspotContacts(company.integrationId, { companyId: company.id, pageSize: 100 })
      .then((r) => {
        if (cancelled) return;
        setContacts(r.data);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err?.message ?? 'Error cargando contactos');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, company]);

  const retry = () => {
    if (!company || !company.integrationId) return;
    setError(null);
    setLoading(true);
    listHubspotContacts(company.integrationId, { companyId: company.id, pageSize: 100 })
      .then((r) => setContacts(r.data))
      .catch((e: Error) => setError(e?.message ?? 'Error'))
      .finally(() => setLoading(false));
  };

  if (!company) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="w-full max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{company.name}</span>
            <Badge variant="outline" className="bg-alerta-suave text-alerta border-alerta-borde">
              HubSpot
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          <div className="bg-alerta-suave border border-alerta-borde text-foreground text-sm p-3 rounded">
            Esta empresa se sincroniza desde HubSpot. Para editar los datos, hacelo en HubSpot
            directamente.
          </div>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground">Datos</h3>
            <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
              <dt className="text-muted-foreground">Dominio</dt>
              <dd>{company.website ?? '—'}</dd>
              <dt className="text-muted-foreground">Teléfono</dt>
              <dd>{company.phone ?? '—'}</dd>
              <dt className="text-muted-foreground">Industria</dt>
              <dd>{company.industry ?? '—'}</dd>
              <dt className="text-muted-foreground">Ciudad</dt>
              <dd>{company.city ?? '—'}</dd>
              <dt className="text-muted-foreground">País</dt>
              <dd>{company.country ?? '—'}</dd>
              <dt className="text-muted-foreground">Última sync</dt>
              <dd>{company.lastSyncAt ? new Date(company.lastSyncAt).toLocaleString() : '—'}</dd>
              <dt className="text-muted-foreground">HubSpot ID</dt>
              <dd className="font-mono text-xs">{company.hubspotCompanyId}</dd>
            </dl>
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground">Contactos asociados</h3>
            {loading ? (
              <p className="text-sm text-muted-foreground py-4">Cargando...</p>
            ) : error ? (
              <div className="text-sm text-destructive py-4 flex items-center gap-2">
                <span>{error}</span>
                <Button variant="link" size="sm" onClick={retry}>
                  Reintentar
                </Button>
              </div>
            ) : contacts.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">Sin contactos asociados.</p>
            ) : (
              <ul className="divide-y border rounded">
                {contacts.map((c) => (
                  <li key={c.id} className="p-3">
                    <p className="font-medium">
                      {[c.firstName, c.lastName].filter(Boolean).join(' ') || '—'}
                    </p>
                    <p className="text-sm text-muted-foreground">{c.email ?? '—'}</p>
                    {(c.jobTitle || c.phone) && (
                      <p className="text-xs text-muted-foreground">
                        {c.jobTitle ?? ''}
                        {c.jobTitle && c.phone ? ' · ' : ''}
                        {c.phone ?? ''}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
